import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/** Pure helpers for the bill processing queue: what an operator sees, and in what order. */

export const WORK_STATES = {
  Unclaimed: 'Unclaimed',
  Working: 'Working',
  ReadyForQa: 'Ready for QA',
  NoBillAvailable: 'No bill available',
  InvalidCredentials: 'Invalid credentials',
  Blocked: 'Blocked',
  ErrorWithAllocation: 'Error with allocation',
  SkippedBiMonthly: 'Skipped, bi-monthly',
  SkippedQuarterly: 'Skipped, quarterly',
  DoNotBill: 'Do not bill',
  SkippedOther: 'Skipped, other'
};

/**
 * States that end this row's cycle. Recording one drops the row out of the default view and
 * releases the claim, because the BA is done with it whether or not a bill exists.
 *
 * The two cadence skips are separate from "no bill available" on purpose: a bi-monthly utility has
 * no bill in half its cycles by design, and recording that as unavailable makes it look late and
 * corrupts the publication-lag prediction.
 */
export const DEAD_END_STATES = ['NoBillAvailable', 'SkippedBiMonthly', 'SkippedQuarterly', 'InvalidCredentials', 'Blocked', 'SkippedOther', 'DoNotBill'];
export const WORK_STATE_COLORS = {
  Unclaimed: 'default',
  Working: 'info',
  ReadyForQa: 'success',
  NoBillAvailable: 'warning',
  InvalidCredentials: 'error',
  Blocked: 'error',
  ErrorWithAllocation: 'error',
  SkippedBiMonthly: 'default',
  SkippedQuarterly: 'default',
  DoNotBill: 'default',
  SkippedOther: 'default'
};
export const workStateLabel = value => WORK_STATES[value] || value || WORK_STATES.Unclaimed;
export const formatCycle = (year, month) => year && month ? new Date(year, month - 1, 1).toLocaleDateString(undefined, {
  month: 'short',
  year: 'numeric'
}) : '';

/** `YYYY-MM-DD` as UTC ms. `new Date(value)` reads back a day early west of Greenwich. */
const parseCalendarDate = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value !== null && value !== void 0 ? value : ''));
  if (!match) return null;
  const _match = _slicedToArray(match, 4),
    year = _match[1],
    month = _match[2],
    day = _match[3];
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
};

/** Days overdue; negative means not yet expected. Null with no history, which is not zero. */
export const overdueDays = function (item) {
  let today = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const expected = parseCalendarDate(item === null || item === void 0 ? void 0 : item.expectedBillDate);
  if (expected === null) return null;
  return Math.round((Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) - expected) / 86400000);
};
export const dueLabel = function (item) {
  let today = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  const days = overdueDays(item, today);
  if (days === null) return 'No history';
  if (days === 0) return 'Due today';
  if (days > 0) return "".concat(days, " day").concat(days === 1 ? '' : 's', " overdue");
  return "In ".concat(Math.abs(days), " day").concat(Math.abs(days) === 1 ? '' : 's');
};
const toIsoDate = utcMs => new Date(utcMs).toISOString().slice(0, 10);

/**
 * The likely service period, prefilled so the operator corrects rather than types.
 *
 * Starts the day after the last bill ended: periods abut, and an overlap double-bills a day. Empty
 * with no history rather than guessing a month that reads authoritative.
 */
export const suggestedPeriod = item => {
  const lastEnd = parseCalendarDate(item === null || item === void 0 ? void 0 : item.lastBillEnd);
  if (lastEnd === null) return {
    startDate: '',
    endDate: ''
  };
  const start = lastEnd + 86400000;
  const cadence = Number(item === null || item === void 0 ? void 0 : item.cadenceDays) > 0 ? Number(item.cadenceDays) : 30;
  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(start + (cadence - 1) * 86400000)
  };
};

/** Why this bill cannot be submitted, or null. Mirrors the server, which re-checks. */
/** Matches the server's guard. A mistyped year would otherwise own this property's prediction. */
const MAX_BILL_DATE_LAG_DAYS = 180;
const DAY_MS = 86400000;
export const rejectBillEntry = function () {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    startDate = _ref.startDate,
    endDate = _ref.endDate,
    amount = _ref.amount,
    billDate = _ref.billDate;
  const start = parseCalendarDate(startDate);
  const end = parseCalendarDate(endDate);
  if (start === null || end === null) return 'Service start and end are both required.';
  if (end < start) return 'The service period ends before it starts.';
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return 'A bill amount is required.';

  // Optional, so an absent one is fine; a present one has to be plausible.
  if (billDate) {
    const billed = parseCalendarDate(billDate);
    if (billed === null) return 'That bill date is not a date.';
    if (billed < start) return 'The bill date is before the service period starts.';
    if ((billed - end) / DAY_MS > MAX_BILL_DATE_LAG_DAYS) {
      return "The bill date is more than ".concat(MAX_BILL_DATE_LAG_DAYS, " days after the service period ends.");
    }
  }
  return null;
};

/**
 * What actually gets POSTed for a bill entry.
 *
 * `fileId` is omitted rather than sent empty: the API binds it to `Guid?`, and `""` is not a Guid,
 * so an empty string is a 400 on a form the operator has already filled in correctly.
 */
export const billEntryPayload = function () {
  let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    startDate = _ref2.startDate,
    endDate = _ref2.endDate,
    amount = _ref2.amount,
    billDate = _ref2.billDate;
  let fileId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  return _objectSpread(_objectSpread({
    startDate,
    endDate,
    amount: Number(amount)
  }, fileId ? {
    fileId
  } : {}), billDate ? {
    billDate
  } : {});
};

/**
 * Why a row is expected when it is, in words a BA can challenge.
 *
 * A bare date invites "the queue is wrong". Naming the sample size and where it came from lets an
 * operator who knows the provider moved its billing day say so.
 */
export const lagExplanation = item => {
  if (!item) return '';
  const lag = item.publicationLagDays;
  if (lag === null || lag === undefined || item.lagBasis === 'none') {
    return 'No bill-date history yet, so this is the end of the service period.';
  }
  const days = lag === 1 ? '1 day' : "".concat(lag, " days");
  if (item.lagBasis === 'provider') {
    return "Usually arrives ".concat(days, " after the period ends, from this provider's other properties.");
  }
  const samples = item.lagSampleCount === 1 ? '1 bill' : "".concat(item.lagSampleCount, " bills");
  return "Usually arrives ".concat(days, " after the period ends, from ").concat(samples, ".");
};

/** Deep link into the allocation modal. Company included: the modal is company-scoped. */
export const applyBillingLink = item => {
  if (!(item !== null && item !== void 0 && item.propertyId)) return null;
  const params = new URLSearchParams({
    propertyId: item.propertyId,
    utilityId: item.utilityId || ''
  });
  if (item.companyId) params.set('companyId', item.companyId);
  if (item.billingCycleId) params.set('billingCycleId', item.billingCycleId);
  return "/billing?".concat(params.toString());
};

/** Columns that must never be exported: a CSV with credentials in it is the tracker again. */
export const CREDENTIAL_FIELDS = ['usernameHint', 'credentialStatus', 'credentialId'];

/** Filtered at the export call, so marking a credential column exportable cannot leak it. */
export const exportableColumns = columns => (columns || []).filter(column => !CREDENTIAL_FIELDS.includes(column.field));

/** Next in the order the operator is looking at, not some server-side ordering they cannot see. */
export const nextItem = (items, currentId) => {
  const list = Array.isArray(items) ? items : [];
  const index = list.findIndex(i => i.allocationTableId === currentId);
  if (index < 0) return list[0] || null;
  return list[index + 1] || null;
};
export const previousItem = (items, currentId) => {
  const list = Array.isArray(items) ? items : [];
  const index = list.findIndex(i => i.allocationTableId === currentId);
  if (index <= 0) return null;
  return list[index - 1];
};